import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { updateRfq } from "../../api/ProcurementClient";
import { AppContext } from "../../AppContextProvider";
import { bumpRfqClientEpoch, mergeRfqIntoList } from "../../utils/mergeRfqIntoList";
import { copyTextToClipboard } from "../../utils/copyTextToClipboard";
import { rememberLastWorkQueueRfq } from "../../utils/purchaseOrderWorkQueue";

interface RfqNoteDialogProps {
  open: boolean;
  rfqId: number;
  rfqReferenceNum: string;
  initialNote: string;
  onClose: () => void;
}

const RfqNoteDialog: React.FC<RfqNoteDialogProps> = ({
  open,
  rfqId,
  rfqReferenceNum,
  initialNote,
  onClose,
}) => {
  const { setRfqs, setSnackbarMsg, setSnackbarErrorMsg } = useContext(AppContext);
  const [note, setNote] = useState(initialNote);
  const [saving, setSaving] = useState(false);
  const noteInputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (open) {
      setNote(initialNote);
    }
  }, [open, initialNote]);

  const focusNoteField = () => {
    const input = noteInputRef.current;
    if (!input) {
      return;
    }
    input.focus();
    const end = input.value.length;
    input.setSelectionRange(end, end);
  };

  const handleSave = async () => {
    setSaving(true);
    bumpRfqClientEpoch(rfqId);
    try {
      const trimmed = note.trim();
      const updated = await updateRfq(rfqId, { note: trimmed || null });
      bumpRfqClientEpoch(rfqId);
      setRfqs((prev) => mergeRfqIntoList(prev, updated));
      rememberLastWorkQueueRfq(rfqId);
      setSnackbarMsg(
        trimmed
          ? `Note saved for RFQ ${rfqReferenceNum}.`
          : `Note removed for RFQ ${rfqReferenceNum}.`,
      );
      onClose();
    } catch (e) {
      bumpRfqClientEpoch(rfqId);
      setSnackbarErrorMsg(
        e instanceof Error ? e.message : "Could not save RFQ note.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      TransitionProps={{ onEntered: focusNoteField }}
    >
      <DialogTitle>RFQ Note</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          multiline
          minRows={4}
          margin="dense"
          label="Note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          disabled={saving}
          inputRef={noteInputRef}
          title={note.length > 0 ? "Double-click to copy" : undefined}
          onDoubleClick={
            note.length > 0
              ? (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  void copyTextToClipboard(note);
                }
              : undefined
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void handleSave()} disabled={saving}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RfqNoteDialog;
